/**
 * This module encapsulates Google One Tap functionality.
 * Use this module as a facade so interactions with Google One Tap library are simplified for any subject interest
 * in accessing Google One Tap capabilities.
 * Google One Tap is a user authentication and sign-in solution provided by Google.
 * It allows users to sign in to websites and apps using their Google account with a single tap,
 * providing a streamlined and secure authentication experience. With Google One Tap, users can
 * easily access websites without the need to remember and enter complex passwords.
 */

const googleOneTapInstance = {
  isGoogleOneTapLoaded: false,
  isGoogleOneTapLoading: false,
  clientId: null,
  onAuthenticated: null,
};

// Interval to sample waiting for the load event; this number is arbitrary.
const googleOneTapLoadSampleRate = 100;

/**
 * If the Google One Tap API fails to load, set the loading and loaded flags to false and reject the promise.
 * @param reject - A function that rejects the promise.
 */
const onGoogleOneTapError = (reject) => {
  googleOneTapInstance.isGoogleOneTapLoading = false;
  googleOneTapInstance.isGoogleOneTapLoaded = false;
  reject(new Error(`Unable to load Google One Tap API.`));
};

/**
 * When the Google One Tap API is loaded, set the isGoogleOneTapLoaded property of the googleOneTapInstance object to
 * true and resolve the promise.
 * @param resolve - A function that is called when the promise is resolved.
 */
const onGoogleOneTapLoaded = (resolve) => {
  window.google.accounts.id.initialize({
    client_id: googleOneTapInstance.clientId,
    callback: googleOneTapInstance.onAuthenticated,
    cancel_on_tap_outside: false,
    // Due to Intelligent Tracking Prevention (ITP), the normal One Tap UX doesn't work on
    // Chrome on iOS, Safari, or Firefox. A different UX is provided instead on these browsers.
    itp_support: true,
    use_fedcm_for_prompt: true,
  });
  googleOneTapInstance.isGoogleOneTapLoaded = true;
  resolve();
};

/**
 * Create a script element, set its source to the Google One Tap source,
 * set its onerror property to a function that rejects the promise,
 * set its onload property to a function that resolves the promise.
 * @param resolve - The function to call when the script has loaded.
 * @param reject - A function that is called when the promise is rejected.
 */
const appendGoogleOneTapScript = (resolve, reject) => {
  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  script.onerror = onGoogleOneTapError.bind(this, reject);
  script.onload = onGoogleOneTapLoaded.bind(this, resolve);
  document.head.appendChild(script);
};

/**
 * If the Google One Tap is loaded, resolve the promise.
 * If not, wait the amount of time from googleOneTapLoadSampleRate and try again.
 * @param resolve - The resolve function of the promise.
 */
const isGoogleOneTapLoaded = (resolve) => {
  if (googleOneTapInstance.isGoogleOneTapLoaded) {
    resolve();
  } else {
    setTimeout(isGoogleOneTapLoaded.bind(null, resolve), googleOneTapLoadSampleRate);
  }
};

/**
 * Wait the amount of time from googleOneTapLoadSampleRate for Google One Tap to load, and then resolve the promise.
 * @returns A promise that will resolve when the Google One Tap API is loaded.
 */
const waitForGoogleOneTap = () =>
  new Promise((resolve) => {
    setTimeout(isGoogleOneTapLoaded.bind(null, resolve), googleOneTapLoadSampleRate);
  });

/**
 * loadGoogleOneTap: if the Google One Tap instance is not loading, then load it.
 * If it is loading, wait for it to load.
 * If it is loaded, resolve the promise
 * @param googleOneTapLoaded - A function that is called when the Google One Tap instance has loaded.
 * @param googleOneTapErrored - A function that will be called if the Google One Tap script fails to load.
 * @returns A promise.
 */
export function loadGoogleOneTap({ onLibraryLoaded, onLibraryErrored, onAuthenticated, clientId }) {
  googleOneTapInstance.clientId = clientId;
  googleOneTapInstance.onAuthenticated = onAuthenticated;

  if (!googleOneTapInstance.isGoogleOneTapLoading) {
    // Set the Google One Tap instance as loading to prevent multiple requests.
    googleOneTapInstance.isGoogleOneTapLoading = true;
    return new Promise(appendGoogleOneTapScript).then(onLibraryLoaded).catch(onLibraryErrored);
  }

  if (googleOneTapInstance.isGoogleOneTapLoaded) {
    // If the Google One Tap instance is already loaded resolve the promise.
    return Promise.resolve().then(onLibraryLoaded).catch(onLibraryErrored);
  }

  // Otherwise wait to review if the Google One Tap instance has loaded.
  return waitForGoogleOneTap().then(onLibraryLoaded).catch(onLibraryErrored);
}

/**
 * showGoogleOneTapModal: this function will prompt the google one tap login modal to the user.
 */
export function showGoogleOneTapModal() {
  window.google.accounts.id.prompt();
}

/**
 * Hides the Google One Tap modal.
 */
export function hideGoogleOneTapModal() {
  window.google?.accounts?.id?.cancel();
}
